import React from "react";
import {
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import DeleteIcon from "@material-ui/icons/Delete";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { fieldLabel, getFieldDefinition } from "./fieldTypes";
import { identifierColumnIndex } from "./layoutOps";

// Lista dos campos do formulário: é aqui que se enxerga a estrutura inteira de
// uma vez e se reordena. Arrastar usa o DnD nativo do HTML5 (sem dependência
// nova); as setas fazem a mesma coisa pelo teclado/clique, que é o caminho que
// funciona em qualquer navegador e no touch.
class Canvas extends React.Component {
  constructor(props) {
    super(props);
    this.state = { dragIndex: null, overIndex: null };
  }

  handleDragStart = (index) => (event) => {
    // Sem isso o Firefox não inicia o arrasto.
    event.dataTransfer.setData("text/plain", String(index));
    event.dataTransfer.effectAllowed = "move";
    this.setState({ dragIndex: index });
  };

  handleDragOver = (index) => (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (this.state.overIndex !== index) this.setState({ overIndex: index });
  };

  handleDrop = (index) => (event) => {
    event.preventDefault();
    const from = Number(event.dataTransfer.getData("text/plain"));
    this.setState({ dragIndex: null, overIndex: null });
    if (Number.isNaN(from) || from === index) return;
    this.props.onMove(from, index);
  };

  handleDragEnd = () => {
    this.setState({ dragIndex: null, overIndex: null });
  };

  // Etiquetas que respondem "o que este campo faz?" sem abrir o inspetor.
  renderBadges = (field) => {
    const { classes } = this.props;
    const badges = [];

    if (field.required === true) {
      badges.push({ label: "Obrigatório", color: "secondary" });
    }
    if (field.identifier === true) {
      badges.push({ label: "Identificador", color: "primary" });
    }
    if (field.hide_pdf === true) {
      badges.push({ label: "Fora do PDF" });
    }
    if (Array.isArray(field.columns)) {
      badges.push({ label: `${field.columns.length} coluna(s)` });
      if (identifierColumnIndex(field) >= 0) {
        badges.push({ label: "Identificador na coluna", color: "primary" });
      }
      if (field.columns.some((column) => column.sum === true)) {
        badges.push({ label: "Com soma" });
      }
      const obrigatorias = field.columns.filter((column) => column.required === true).length;
      if (obrigatorias > 0) {
        badges.push({ label: `${obrigatorias} coluna(s) obrigatória(s)`, color: "secondary" });
      }
    }
    if (Array.isArray(field.options) && field.type !== "select-api") {
      badges.push({ label: `${field.options.length} opção(ões)` });
    }
    if (field.multi === true) {
      badges.push({ label: "Múltipla escolha" });
    }
    if (field.type === "form-link") {
      badges.push({
        label: field.target_form_name
          ? `→ ${field.target_form_name}`
          : "sem formulário-alvo",
        color: field.target_form_name ? "default" : "secondary",
      });
    }
    if (Array.isArray(field.extra_fields) && field.extra_fields.length > 0) {
      badges.push({ label: `${field.extra_fields.length} campo(s) por item` });
    }

    return badges.map((badge) => (
      <Chip
        key={badge.label}
        size="small"
        label={badge.label}
        color={badge.color || "default"}
        className={classes.badge}
      />
    ));
  };

  renderField = (field, index) => {
    const { classes, selectedIndex, fields } = this.props;
    const definition = getFieldDefinition(field.type);
    const selected = selectedIndex === index;
    const over = this.state.overIndex === index && this.state.dragIndex !== index;

    return (
      <Paper
        key={`${field.type}-${index}`}
        variant="outlined"
        draggable
        onDragStart={this.handleDragStart(index)}
        onDragOver={this.handleDragOver(index)}
        onDrop={this.handleDrop(index)}
        onDragEnd={this.handleDragEnd}
        onClick={() => this.props.onSelect(index)}
        className={`${classes.card} ${selected ? classes.cardSelected : ""} ${
          over ? classes.cardOver : ""
        }`}
      >
        <DragIndicatorIcon className={classes.drag} fontSize="small" />
        <div className={classes.info}>
          <Typography variant="body2" className={classes.label}>
            {fieldLabel(field)}
          </Typography>
          <Typography variant="caption" className={classes.type}>
            {index + 1}. {definition.label}
          </Typography>
          <div>{this.renderBadges(field)}</div>
        </div>
        <div className={classes.actions}>
          <Tooltip title="Mover para cima">
            <span>
              <IconButton
                size="small"
                disabled={index === 0}
                onClick={(event) => {
                  event.stopPropagation();
                  this.props.onMove(index, index - 1);
                }}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Mover para baixo">
            <span>
              <IconButton
                size="small"
                disabled={index === fields.length - 1}
                onClick={(event) => {
                  event.stopPropagation();
                  this.props.onMove(index, index + 1);
                }}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Duplicar">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                this.props.onDuplicate(index);
              }}
            >
              <FileCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                this.props.onRemove(index);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </Paper>
    );
  };

  render() {
    const { classes, fields } = this.props;

    if (fields.length === 0) {
      return (
        <div className={classes.empty}>
          <Typography variant="body2" color="textSecondary">
            Nenhum campo ainda. Escolha um tipo na coluna da esquerda para começar.
          </Typography>
        </div>
      );
    }

    return <div className={classes.container}>{fields.map(this.renderField)}</div>;
  }
}

const styles = (theme) => ({
  container: {
    padding: theme.spacing(1),
  },
  empty: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
  },
  card: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    borderLeft: "4px solid transparent",
  },
  cardSelected: {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: "#f5f8ff",
  },
  cardOver: {
    borderTop: `2px solid ${theme.palette.primary.main}`,
  },
  drag: {
    color: theme.palette.text.disabled,
    cursor: "grab",
    marginRight: theme.spacing(1),
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontWeight: 600,
    wordBreak: "break-word",
  },
  type: {
    color: theme.palette.text.secondary,
    display: "block",
  },
  badge: {
    marginRight: 4,
    marginTop: 4,
  },
  actions: {
    display: "flex",
    alignItems: "center",
  },
});

export default withStyles(styles)(Canvas);
